import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { HomeHeader } from '../home/HomeHeader'
import { useAuth } from '../../contexts/AuthContext'
import { Card, CardContent } from '../ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs'
import { Button } from '../ui/button'
import { ChevronLeft } from 'lucide-react'
import { BooksTable } from './BooksTable'
import { TranslationsTable } from './TranslationsTable'
import { UserTranslationsTable } from './UserTranslationsTable'
import { JobsTable } from './JobsTable'

export function AdminManagementPage() {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [activeTab, setActiveTab] = useState('books')

  // Initialize tab from URL search params
  useEffect(() => {
    const tab = searchParams.get('tab')
    if (tab && ['books', 'translations', 'user-translations', 'jobs'].includes(tab)) {
      setActiveTab(tab)
    }
  }, [searchParams])

  const handleLogout = async () => {
    try {
      await logout()
      navigate('/')
    } catch (error) {
      console.error('Logout failed:', error)
    }
  }

  return (
    <div className="admin-management-page min-h-screen bg-background">
      <HomeHeader user={user} onLogout={handleLogout} />
      
      <div className="container mx-auto px-4 py-6">
        {/* Header */}
        <div className="header-section mb-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/admin')}
            className="mb-4"
          >
            <ChevronLeft className="mr-2 h-4 w-4" />
            Back to Dashboard
          </Button>
          
          <div className="flex items-center gap-3">
            <div className="w-2 h-8 bg-primary rounded-full"></div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">Data Management</h1>
              <p className="text-sm text-muted-foreground">
                Manage books, translations, and jobs with bulk operations
              </p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs 
          value={activeTab} 
          onValueChange={(value) => {
            setActiveTab(value)
            // Update URL with tab parameter and clear page when switching tabs
            const newSearchParams = new URLSearchParams()
            newSearchParams.set('tab', value)
            // Don't preserve 'page' param - each tab has its own pagination
            navigate(`/admin/management?${newSearchParams.toString()}`, { replace: true })
          }} 
          className="management-tabs space-y-6"
        >
          <TabsList className="grid w-full max-w-2xl grid-cols-4">
            <TabsTrigger value="books">Books</TabsTrigger>
            <TabsTrigger value="translations">Translations</TabsTrigger>
            <TabsTrigger value="user-translations">User Translations</TabsTrigger>
            <TabsTrigger value="jobs">Jobs</TabsTrigger>
          </TabsList>

          <TabsContent value="books" className="books-tab space-y-4">
            <Card>
              <CardContent className="pt-6">
                <BooksTable isActive={activeTab === 'books'} />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="translations" className="translations-tab space-y-4">
            <Card>
              <CardContent className="pt-6">
                <TranslationsTable isActive={activeTab === 'translations'} />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="user-translations" className="user-translations-tab space-y-4">
            <Card>
              <CardContent className="pt-6">
                <UserTranslationsTable isActive={activeTab === 'user-translations'} />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="jobs" className="jobs-tab space-y-4">
            <Card>
              <CardContent className="pt-6">
                <JobsTable isActive={activeTab === 'jobs'} />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  )
}

